import { ButtonGroup, Flex, Text } from "@chakra-ui/react";
import { zodResolver } from "@hookform/resolvers/zod";
import { FormProvider, useForm } from "react-hook-form";

import { Restricted } from "@/app/components/Restricted";
import FormReset from "@/common/components/Form/FormReset";
import { FormRootError } from "@/common/components/Form/FormRootError";
import FormSubmit from "@/common/components/Form/FormSubmit";
import { useFormMutationSubmitHandler } from "@/common/hooks/useApiForm";
import { useSlugParam } from "@/common/hooks/useParam";
import { FormEventBasedFilterInputs } from "@/feature/components/Form/FormEventBasedFilterInputs";
import { useFeatureData } from "@/feature/data/useFeatureData";
import { useFeatureUpdateMutation } from "@/feature/data/useFeatureUpdateMutation";
import { PatchFeatureArgsSchema } from "@reflag/shared/featureAPI";
import { z } from "@reflag/shared/utils/zod";

type FormType = z.input<typeof PatchFeatureArgsSchema>;

export function SettingsUsage() {
  const { featureId } = useSlugParam("feature");
  const { data: feature } = useFeatureData(featureId!);

  const defaultValues = {
    customEventSelectors: feature?.customEventSelectors ?? [],
  };

  const form = useForm<FormType>({
    resolver: zodResolver(PatchFeatureArgsSchema),
    mode: "onChange",
    defaultValues: {
      ...defaultValues,
    },
  });

  const updateMutation = useFeatureUpdateMutation(featureId!);
  const submitHandler = useFormMutationSubmitHandler(
    form,
    updateMutation,
    (result) => {
      form.reset({
        source: result.source,
        customEventSelectors: result.customEventSelectors || [],
        usingItAttributeFilter: result.usingItAttributeFilter || [],
        adoptionEvaluationStrategy: result.adoptionEvaluationStrategy,
        adoptionWindowSizeInDays: result.adoptionWindowSizeInDays,
        adoptionStrategyEventCountMinEventCount:
          result.adoptionStrategyEventCountMinEventCount,
        adoptionStrategyFrequencyMinDaysCount:
          result.adoptionStrategyFrequencyMinDaysCount,
      });
    },
    {
      prepareVariables: (values) => ({
        id: featureId!,
        ...values,
      }),
    },
  );

  return (
    <Flex direction="column" maxW="mediumForm">
      <Restricted alertStyle="callout" feature={feature} role="contributor">
        <FormProvider {...form}>
          <Flex as="form" direction={"column"} gap={4} onSubmit={submitHandler}>
            <Text fontSize="md" fontWeight="medium">
              Track events
            </Text>
            <FormEventBasedFilterInputs
              featureKey={feature?.key}
              name="customEventSelectors"
              w="full"
            />
            <FormRootError />

            <ButtonGroup>
              <FormSubmit showPostSubmit />
              <FormReset />
            </ButtonGroup>
          </Flex>
        </FormProvider>
      </Restricted>
    </Flex>
  );
}
